'use client';

import { useState } from 'react';
import { Pencil, Plus, Shield, Trash2, UserCog } from 'lucide-react';
import { AdminModal } from '@/components/admin/AdminModal';
import { useToast } from '@/components/admin/Toast';
import { ClientDate } from '../ClientDate';
import type { PlatformAdminUser } from '../types';

type Props = {
  users: PlatformAdminUser[];
  loading: boolean;
  currentUserId?: string;
  onRefresh: () => void;
};

export function PlatformUsersPanel({ users, loading, currentUserId, onRefresh }: Props) {
  const { toast } = useToast();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'super_admin' });
  const [editing, setEditing] = useState<PlatformAdminUser | null>(null);
  const [editForm, setEditForm] = useState({ name: '', password: '', is_active: true });
  const [saving, setSaving] = useState(false);

  const createUser = async () => {
    setSaving(true);
    const res = await fetch('/api/super-admin/platform-users', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    setSaving(false);
    if (res.ok) {
      toast('Admin de plataforma criado');
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', role: 'super_admin' });
      onRefresh();
    } else {
      const data = await res.json().catch(() => ({}));
      toast(data.error || 'Erro ao criar', 'error');
    }
  };

  const deactivate = async (id: string) => {
    const res = await fetch(`/api/super-admin/platform-users/${id}`, { method: 'DELETE' });
    if (res.ok) {
      toast('Conta desativada');
      onRefresh();
    } else {
      const data = await res.json().catch(() => ({}));
      toast(data.error || 'Erro', 'error');
    }
  };

  const startEdit = (user: PlatformAdminUser) => {
    setEditing(user);
    setEditForm({ name: user.name, password: '', is_active: user.is_active });
  };

  const updateUser = async () => {
    if (!editing) return;
    setSaving(true);
    const res = await fetch(`/api/super-admin/platform-users/${editing.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: editForm.name,
        is_active: editForm.is_active,
        ...(editForm.password ? { password: editForm.password } : {}),
      }),
    });
    setSaving(false);
    if (res.ok) {
      toast('Admin de plataforma atualizado');
      setEditing(null);
      onRefresh();
    } else {
      const data = await res.json().catch(() => ({}));
      toast(data.error || 'Erro ao atualizar', 'error');
    }
  };

  if (loading) {
    return <p className="text-sm text-ink-500">A carregar administradores...</p>;
  }

  return (
    <div className="space-y-4">
      <div className="sa-toolbar">
        <p className="text-sm text-ink-500">
          Contas com acesso ao painel de plataforma (okai.pt/super-admin).
        </p>
        <button type="button" onClick={() => setShowCreate(true)} className="btn btn-sm bg-brand text-white">
          <Plus className="w-4 h-4" /> Novo admin
        </button>
      </div>

      <div className="sa-table-wrap">
        <table className="sa-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Role</th>
              <th>Ultimo login</th>
              <th>Estado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-ink-900 text-white flex items-center justify-center">
                      <Shield className="w-4 h-4" />
                    </div>
                    <span className="font-medium">{u.name}</span>
                    {u.id === currentUserId && <span className="sa-tag">tu</span>}
                  </div>
                </td>
                <td className="font-mono text-sm">{u.email}</td>
                <td><span className="sa-tag">{u.role}</span></td>
                <td className="text-sm text-ink-500">
                  <ClientDate value={u.last_login_at} />
                </td>
                <td>
                  <span className={`sa-status sa-status-${u.is_active ? 'ok' : 'muted'}`}>
                    {u.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => startEdit(u)} className="text-ink-400 hover:text-brand p-1" title="Editar">
                      <Pencil className="w-4 h-4" />
                    </button>
                    {u.is_active && u.id !== currentUserId && (
                      <button
                        type="button"
                        onClick={() => deactivate(u.id)}
                        className="text-ink-400 hover:text-danger p-1"
                        title="Desativar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AdminModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="Novo administrador de plataforma"
        description="Acesso total ao super-admin em okai.pt"
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowCreate(false)} className="btn btn-secondary btn-sm">Cancelar</button>
            <button type="button" onClick={createUser} disabled={saving} className="btn btn-sm bg-brand text-white">
              <UserCog className="w-4 h-4" /> Criar
            </button>
          </div>
        }
      >
        <div className="space-y-3">
          <input type="text" placeholder="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-field" />
          <input type="email" placeholder="email@okai.pt" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-field" />
          <input type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-field" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input-field">
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
      </AdminModal>

      <AdminModal
        open={!!editing}
        onClose={() => setEditing(null)}
        title="Editar administrador de plataforma"
        description={editing?.email}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setEditing(null)} className="btn btn-secondary btn-sm">Cancelar</button>
            <button type="button" onClick={updateUser} disabled={saving} className="btn btn-sm bg-brand text-white">Guardar</button>
          </div>
        }
      >
        <div className="space-y-3">
          <input type="text" placeholder="Nome" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="input-field" />
          <input type="password" placeholder="Nova password (deixar vazio para manter)" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} className="input-field" />
          {editing?.id !== currentUserId && (
            <label className="flex items-center gap-2 text-sm text-ink-700">
              <input type="checkbox" checked={editForm.is_active} onChange={(e) => setEditForm({ ...editForm, is_active: e.target.checked })} />
              Conta ativa
            </label>
          )}
        </div>
      </AdminModal>
    </div>
  );
}
